import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useRoute, useRouter } from 'vue-router'

export type BaseDashboard = {
  title: string
  dashboard_url: string
  allowed_member_ids?: string[] | null
}

export type BaseAnalysis = {
  title: string
  menu_position: number
  icon: string | undefined
  dashboards: BaseDashboard[]
  allowed_member_ids?: string[] | null
}

export interface Dashboard extends BaseDashboard {
  id: string
}

export interface Analysis extends BaseAnalysis {
  id: string
  dashboards: Dashboard[]
}

export type DashboardResponse = {
  analyses: Analysis[]
  reports_enabled: boolean
}

type ErrorResponse = {
  detail: string
}

type DashboardIframeResponse = {
  iframe_url: string
}

type OmniFilter = {
  asJsonUrlSearchParam: string
  filter: {
    fieldName: string
    hidden: boolean
    is_negative: boolean
    kind: string
    label: string
    type: string
    values: string[]
  }
  urlId: string
}
type OmniFilterChanged = {
  action: string
  next: OmniFilter
  prev: OmniFilter
  urlId: string
}
export type OmniFilterChangedPayload = Record<string, OmniFilterChanged>
type UserFilters = Record<string, string>

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || ''

export const useDashboards = defineStore('dashboards', () => {
  const auth = useAuthStore()
  const route = useRoute()
  const router = useRouter()
  const analysis = ref<Analysis[]>([])
  const reportsEnabled = ref<boolean>(false)

  async function fetchDashboards(): Promise<void> {
    return await fetch(`${BACKEND_URL}/api/dashboards`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      let data = await res.json()
      if (!res.ok) {
        // Redirect to disabled page if org is inactive
        if (res.status === 403 && (data as ErrorResponse).detail === 'Organization is inactive') {
          router.replace({ name: 'organization-disabled' })
          throw new Error('Organization is inactive')
        }
        throw new Error((data as ErrorResponse).detail || 'Error fetching dashboards')
      }
      data = data as DashboardResponse
      analysis.value = data.analyses
      reportsEnabled.value = data.reports_enabled
    })
  }

  async function fetchPreviewDashboards(organizationId: string): Promise<void> {
    return await fetch(`${BACKEND_URL}/api/admin/organizations/${organizationId}/dashboards`, {
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${auth.stytchToken}`,
      },
    }).then(async (res) => {
      let data = await res.json()
      if (!res.ok) {
        throw new Error((data as ErrorResponse).detail || 'Error fetching preview dashboards')
      }
      data = data as DashboardResponse
      analysis.value = data.analyses
      reportsEnabled.value = data.reports_enabled
    })
  }

  function getDashboard(id: string): Dashboard | null {
    for (const item of analysis.value) {
      const dashboard = item.dashboards.find((d) => d.id === id)
      if (dashboard) {
        return dashboard
      }
    }
    return null
  }

  async function _fetchDashboardIframe(
    id: string,
    colorMode: string | undefined = undefined,
    filters: string | null = null,
  ): Promise<string | null> {
    let url = `${BACKEND_URL}/api/dashboards/${id}/embed`
    const urlParams = []
    if (colorMode) {
      urlParams.push(`color_mode=${colorMode}`)
    }
    if (filters) {
      urlParams.push(filters)
    }

    url += `?${urlParams.join('&')}`
    let response: Response
    try {
      response = await fetch(url, {
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${auth.stytchToken}`,
        },
      })
    } catch {
      // ponytail: network failure (offline, DNS, CORS) — return null, caller handles it
      return null
    }

    let data = await response.json()
    if (!response.ok) {
      const err = (data as ErrorResponse).detail || 'Error fetching dashboard iframe'
      console.log(err)
      return null
    }
    data = data as DashboardIframeResponse
    return data.iframe_url
  }

  async function getDashboardIframe(
    id: string,
    colorMode: string | undefined = undefined,
  ): Promise<string | null> {
    const dashboard = getDashboard(id)
    if (dashboard === null) return null

    // it's not possible to reuse the iframe URL twice, so there's no use
    // caching this on the dashboard object
    return await _fetchDashboardIframe(id, colorMode, getUserFiltersAsUrlParams())
  }

  function _getUserFiltersKey(): string {
    const routeOrgId = route.params.orgId as string | undefined

    // we use a key with a suffix that depends on the org because we want this to
    // work in Admin Peview Mode too
    return routeOrgId
      ? `omniUserFilters-${routeOrgId}`
      : `omniUserFilters-${auth.organization?.organization_id}`
  }

  function getUserFilters(): UserFilters | null {
    return JSON.parse(window.localStorage.getItem(_getUserFiltersKey()) || 'null')
  }

  function getUserFiltersAsUrlParams(): string | null {
    const filters = getUserFilters()
    if (filters === null) return null

    return Object.values(filters).join('&')
  }

  function updateUserFilters(filtersPayload: OmniFilterChangedPayload) {
    const filters = getUserFilters() || {}
    for (const [filterField, { next }] of Object.entries(filtersPayload)) {
      filters[filterField] = next.asJsonUrlSearchParam
    }

    window.localStorage.setItem(_getUserFiltersKey(), JSON.stringify(filters))
  }

  return {
    analysis,
    reportsEnabled,
    fetchDashboards,
    fetchPreviewDashboards,
    getDashboard,
    getDashboardIframe,
    updateUserFilters,
  }
})
